import React, { useState, useEffect } from 'react';
import { fetchCountryCodes, fetchCurrencies } from "../../../services/Http/Api";
import PhoneNumberInput from "../PhoneNumberInput";
import CitySearchSelect from "../CitySearchSelect";
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import MainButton from '../../../shared/layout/buttons/MainButton';
import {
    StyledInput,
    StyledTextArea,
    StyledLabel,
    ErrorText,
    FormGroup
} from '../../../shared/layout/forms/FormElements';
import { SearchableSelect } from '../../../shared/layout/forms/AppSelect';
import CountrySelect from '../../../shared/layout/forms/CountrySelect';

const VenueForm = ({ venue, onSave }) => {
    const { t } = useTranslation();
    const [countryCodes, setCountryCodes] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [errors, setErrors] = useState({});
    const [socialLinks, setSocialLinks] = useState({
        twitter: '',
        facebook: '',
        instagram: ''
    });

    const [venueData, setVenueData] = useState({
        name: venue?.name || '',
        description: venue?.description || '',
        email: venue?.email || '',
        phone: venue?.phone || '',
        phone_country_id: venue?.phone_country_id ? String(venue.phone_country_id) : '',
        country_id: venue?.country_id || '',
        city_id: venue?.city_id || '',
        address: venue?.address || '',
        city: venue?.city || '',
        currency_id: venue?.currency_id || '',
        website: venue?.website || '',
        latitude: venue?.latitude || '',
        longitude: venue?.longitude || '',
        organization_id: venue?.organization_id || ''
    });

    useEffect(() => {
        if (venue?.social_media_links) {
            try {
                const parsedLinks = JSON.parse(venue.social_media_links);
                setSocialLinks({
                    twitter: parsedLinks.twitter || '',
                    facebook: parsedLinks.facebook || '',
                    instagram: parsedLinks.instagram || ''
                });
            } catch (e) {
                console.error("Error parsing social media links", e);
            }
        }
    }, [venue]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetchCountryCodes();
                setCountryCodes(response);
            } catch (error) {
                console.error('Error fetching country codes:', error);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const loadCurrencies = async () => {
            try {
                const response = await fetchCurrencies();
                const list = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
                setCurrencies(list);
            } catch (error) {
                console.error('Error fetching currencies:', error);
                setCurrencies([]);
            }
        };
        loadCurrencies();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setVenueData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handlePhoneNumberChange = (data) => {
        setVenueData((prevData) => ({
            ...prevData,
            phone: data.phone,
            phone_country_id: data.phoneCountryCode ? String(data.phoneCountryCode) : ''
        }));
    };

    const handleCountryChange = (_country, value) => {
        setVenueData((prevData) => ({
            ...prevData,
            country_id: value || '',
            city: '',
            city_id: '',
        }));
    };

    const handleSocialChange = (e) => {
        const { name, value } = e.target;
        setSocialLinks((prevLinks) => ({ ...prevLinks, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const nextErrors = {};
        if (!venueData.name.trim()) {
            nextErrors.name = t('Venues.VenueForm.errors.name_required');
        }
        if (!venueData.email.trim()) {
            nextErrors.email = t('Venues.VenueForm.errors.email_required');
        } else if (!/^\S+@\S+\.\S+$/.test(venueData.email)) {
            nextErrors.email = t('Venues.VenueForm.errors.email_invalid');
        }
        if (!venueData.address.trim()) {
            nextErrors.address = t('Venues.VenueForm.errors.address_required');
        }
        if (!venueData.city.trim() && !venueData.city_id) {
            nextErrors.city = t('Venues.VenueForm.errors.city_required');
        }
        if (!venueData.phone.trim()) {
            nextErrors.phone = t('Venues.VenueForm.errors.phone_required');
        }
        if (!venueData.phone_country_id) {
            nextErrors.phone_country_id = t('Venues.VenueForm.errors.phone_country_required');
        }
        if (!venueData.country_id) {
            nextErrors.country_id = t('Venues.VenueForm.errors.country_required');
        }
        // Website is optional, but if provided, validate format
        if (venueData.website.trim()) {
            // Accept URLs with or without protocol (http/https)
            const websitePattern = /^(https?:\/\/)?(www\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z]{2,})+([\/\w\-._~:?#[\]@!$&'()*+,;=%]*)?$/i;
            if (!websitePattern.test(venueData.website)) {
                nextErrors.website = t('Venues.VenueForm.errors.website_invalid');
            }
        }
        if (venueData.latitude) {
            const latitudeValue = Number(venueData.latitude);
            if (Number.isNaN(latitudeValue)) {
                nextErrors.latitude = t('Venues.VenueForm.errors.latitude_invalid');
            } else if (latitudeValue < -90 || latitudeValue > 90) {
                nextErrors.latitude = t('Venues.VenueForm.errors.latitude_range');
            }
        }
        if (venueData.longitude) {
            const longitudeValue = Number(venueData.longitude);
            if (Number.isNaN(longitudeValue)) {
                nextErrors.longitude = t('Venues.VenueForm.errors.longitude_invalid');
            } else if (longitudeValue < -180 || longitudeValue > 180) {
                nextErrors.longitude = t('Venues.VenueForm.errors.longitude_range');
            }
        }
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            return;
        }
        const fullVenueData = {
            ...venueData,
            social_media_links: JSON.stringify(socialLinks)
        };
        onSave(fullVenueData);
    };

    return (
        <FormContainer onSubmit={handleSubmit}>
            <FormSection>
                <SectionHeader>{t('Venues.VenueForm.basic_info')}</SectionHeader>
                <SectionBody>
                    <FormGroup>
                        <StyledLabel htmlFor="venue-name">{t('Venues.VenueForm.name_label')}</StyledLabel>
                        <StyledInput
                            type="text"
                            name="name"
                            id="venue-name"
                            value={venueData.name}
                            onChange={handleChange}
                            placeholder={t('Venues.VenueForm.name_placeholder')}
                            $error={!!errors.name}
                        />
                        {errors.name && <ErrorText>{errors.name}</ErrorText>}
                    </FormGroup>
                    <FormGroup>
                        <StyledLabel htmlFor="venue-description">{t('Venues.VenueForm.description_label')}</StyledLabel>
                        <StyledTextArea
                            name="description"
                            id="venue-description"
                            value={venueData.description}
                            onChange={handleChange}
                            placeholder={t('Venues.VenueForm.description_placeholder')}
                        />
                    </FormGroup>
                    <FormGrid>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-email">{t('Venues.VenueForm.email_label')}</StyledLabel>
                            <StyledInput
                                type="email"
                                name="email"
                                id="venue-email"
                                value={venueData.email}
                                onChange={handleChange}
                                placeholder={t('Venues.VenueForm.email_placeholder')}
                                $error={!!errors.email}
                            />
                            {errors.email && <ErrorText>{errors.email}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-website">{t('Venues.VenueForm.website_label')}</StyledLabel>
                            <StyledInput
                                type="text"
                                name="website"
                                id="venue-website"
                                value={venueData.website}
                                onChange={handleChange}
                                placeholder={t('Venues.VenueForm.website_placeholder') || 'example.com'}
                                $error={!!errors.website}
                            />
                            {errors.website && <ErrorText>{errors.website}</ErrorText>}
                        </FormGroup>
                    </FormGrid>
                </SectionBody>
            </FormSection>

            <FormSection>
                <SectionHeader>{t('Venues.VenueForm.location_details')}</SectionHeader>
                <SectionBody>
                    <SingleColumnGrid>
                        <FormGroup>
                            <StyledLabel>{t('Venues.VenueForm.phone_label')}</StyledLabel>
                            <PhoneNumberInput
                                countryCodes={countryCodes}
                                phoneValue={venueData.phone}
                                phoneCountryValue={venueData.phone_country_id}
                                onChange={handlePhoneNumberChange}
                            />
                            {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
                            {errors.phone_country_id && <ErrorText>{errors.phone_country_id}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-country">{t('Venues.VenueForm.country_label')}</StyledLabel>
                            <CountrySelect
                                countries={countryCodes}
                                value={venueData.country_id}
                                onChange={handleCountryChange}
                                inputId="venue-country"
                                placeholder={t('Venues.VenueForm.select_country')}
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                            {errors.country_id && <ErrorText>{errors.country_id}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-currency">
                                {t('Venues.VenueForm.currency_label', 'Currency')}
                            </StyledLabel>
                            <SearchableSelect
                                inputId="venue-currency"
                                placeholder={t('Venues.VenueForm.select_currency', 'Select currency')}
                                isClearable
                                isSearchable
                                styles={customSelectStyles}
                                options={currencies.map((currency) => ({
                                    value: currency.id,
                                    label: `${currency.code || currency.key || 'CUR'} - ${currency.name || 'Currency'}`,
                                }))}
                                value={currencies
                                    .map((currency) => ({
                                        value: currency.id,
                                        label: `${currency.code || currency.key || 'CUR'} - ${currency.name || 'Currency'}`,
                                    }))
                                    .find((option) => Number(option.value) === Number(venueData.currency_id)) || null}
                                onChange={(_, __, option) => {
                                    setVenueData((prevData) => ({
                                        ...prevData,
                                        currency_id: option ? option.value : '',
                                    }));
                                }}
                            />
                        </FormGroup>
                    </SingleColumnGrid>
                    <FormGrid>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-address">{t('Venues.VenueForm.address_label')}</StyledLabel>
                            <StyledInput
                                type="text"
                                name="address"
                                id="venue-address"
                                value={venueData.address}
                                onChange={handleChange}
                                placeholder={t('Venues.VenueForm.address_placeholder')}
                                $error={!!errors.address}
                            />
                            {errors.address && <ErrorText>{errors.address}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-city">{t('Venues.VenueForm.city_label')}</StyledLabel>
                            <CitySearchSelect
                                countryId={venueData.country_id || null}
                                cityId={venueData.city_id || null}
                                value={venueData.city}
                                placeholder={t('Venues.VenueForm.city_placeholder')}
                                onChange={(city) =>
                                    setVenueData((prevData) => ({
                                        ...prevData,
                                        city: city?.name || '',
                                        city_id: city?.id || '',
                                    }))
                                }
                            />
                            {errors.city && <ErrorText>{errors.city}</ErrorText>}
                        </FormGroup>
                    </FormGrid>
                    <FormGrid>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-latitude">{t('Venues.VenueForm.latitude_label')}</StyledLabel>
                            <StyledInput
                                type="text"
                                name="latitude"
                                id="venue-latitude"
                                value={venueData.latitude}
                                onChange={handleChange}
                                placeholder={t('Venues.VenueForm.latitude_placeholder')}
                                $error={!!errors.latitude}
                            />
                            {errors.latitude && <ErrorText>{errors.latitude}</ErrorText>}
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-longitude">{t('Venues.VenueForm.longitude_label')}</StyledLabel>
                            <StyledInput
                                type="text"
                                name="longitude"
                                id="venue-longitude"
                                value={venueData.longitude}
                                onChange={handleChange}
                                placeholder={t('Venues.VenueForm.longitude_placeholder')}
                                $error={!!errors.longitude}
                            />
                            {errors.longitude && <ErrorText>{errors.longitude}</ErrorText>}
                        </FormGroup>
                    </FormGrid>
                </SectionBody>
            </FormSection>

            <FormSection>
                <SectionHeader>{t('Venues.VenueForm.social_media')}</SectionHeader>
                <SectionBody>
                    <FormGrid>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-twitter">{t('Venues.VenueForm.twitter_label')}</StyledLabel>
                            <StyledInput
                                type="text"
                                name="twitter"
                                id="venue-twitter"
                                value={socialLinks.twitter}
                                onChange={handleSocialChange}
                                placeholder={t('Venues.VenueForm.twitter_placeholder')}
                            />
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel htmlFor="venue-facebook">{t('Venues.VenueForm.facebook_label')}</StyledLabel>
                            <StyledInput
                                type="text"
                                name="facebook"
                                id="venue-facebook"
                                value={socialLinks.facebook}
                                onChange={handleSocialChange}
                                placeholder={t('Venues.VenueForm.facebook_placeholder')}
                            />
                        </FormGroup>
                    </FormGrid>
                    <FormGroup>
                        <StyledLabel htmlFor="venue-instagram">{t('Venues.VenueForm.instagram_label')}</StyledLabel>
                        <StyledInput
                            type="text"
                            name="instagram"
                            id="venue-instagram"
                            value={socialLinks.instagram}
                            onChange={handleSocialChange}
                            placeholder={t('Venues.VenueForm.instagram_placeholder')}
                        />
                    </FormGroup>
                </SectionBody>
            </FormSection>
            <FormFooter>
                <MainButton type="submit" style={{ width: '100%' }}>
                    {t('Venues.VenueForm.save_button')}
                </MainButton>
            </FormFooter>
        </FormContainer>
    );
};

export default VenueForm;

// Styled Components
const FormContainer = styled.form`
    background-color: transparent;
    padding: 0;
`;

const FormSection = styled.section`
    background: #fdfdff;
    border: 1px solid #eef0f7;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
`;

const SectionHeader = styled.h4`
    margin: 0 0 1.25rem;
    color: #1a1a1a;
    font-size: 1.1rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #f1f3f9;
        margin-left: 0.9375rem;
    }
`;

const SectionBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const SingleColumnGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.25rem;
`;

const FormFooter = styled.div`
    margin-top: 0.625rem;
`;

const customSelectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#821692' : '#e5e7eb',
        borderWidth: '1.5px',
        boxShadow: state.isFocused ? '0 0 0 4px rgba(130, 22, 146, 0.1)' : 'none',
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        minHeight: '46px',
        padding: '0.125rem',
        '&:hover': { borderColor: state.isFocused ? '#821692' : '#d1d5db' },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? '#821692'
            : state.isFocused
                ? 'rgba(130, 22, 146, 0.08)'
                : '#fff',
        color: state.isSelected ? '#fff' : '#1f2937',
        cursor: 'pointer',
        padding: '0.625rem 0.875rem',
        fontSize: '0.95rem',
        '&:active': {
            backgroundColor: 'rgba(130, 22, 146, 0.15)',
        },
    }),
    singleValue: (base) => ({
        ...base,
        color: '#1f2937',
        fontSize: '0.95rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#9ca3af',
        fontSize: '0.95rem',
    }),
};
